import { PlusOutlined } from '@ant-design/icons';
import { Button, Empty } from 'antd';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import DataTagCard from '../../molecules/DataTagCard';

/**
 * DataTagCardGrid Organism - Grid layout for DataTag cards with drag & drop functionality
 * 
 * @param {Array} filteredTags - Filtered array of data tags
 * @param {Array} allTags - All tags (for empty state check)
 * @param {Function} onDragEnd - Drag and drop end handler
 * @param {Array} pinnedTags - Array of pinned tag IDs
 * @param {Function} handlePinTag - Function to handle pin/unpin
 * @param {Object} permission - Permission object
 * @param {Function} onEditDataTag - Function to handle edit
 * @param {Function} onViewDataTag - Function to handle view
 * @param {Function} onViewDataTagHistory - Function to handle history view
 * @param {Function} onDeleteDataTag - Function to handle delete
 * @param {Function} getBusinessAreaColors - Function to get business area colors
 * @param {Function} onCreateNewDataTag - Function to create new tag (for empty state)
 */
const DataTagCardGrid = ({
  filteredTags,
  allTags,
  onDragEnd,
  pinnedTags,
  handlePinTag,
  permission,
  onEditDataTag,
  onViewDataTag,
  onViewDataTagHistory,
  onDeleteDataTag,
  getBusinessAreaColors,
  onCreateNewDataTag,
}) => {
  // Empty state when no tags at all
  if (allTags.length === 0) {
    return (
      <div className="flex justify-center items-center h-96">
        <Empty 
          description="No data tags available"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          {permission.canAdd && (
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={onCreateNewDataTag}
            >
              Create First Tag
            </Button>
          )}
        </Empty>
      </div>
    );
  }

  // Empty state when no filtered results
  if (filteredTags.length === 0) {
    return (
      <div className="flex justify-center items-center h-96">
        <Empty 
          description="No tags match your current filters"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  // Render grid with drag and drop
  return (
    <div 
      className="overflow-y-auto overflow-x-hidden" 
      style={{ 
        height: 'calc(100vh - 250px)',
        scrollbarWidth: 'thin',
        scrollbarColor: '#eaeaea transparent'
      }}
    >
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="tags">
        {(provided) => (
          <div 
            {...provided.droppableProps} 
            ref={provided.innerRef} 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-2"
          >
            {filteredTags.map((dataTag, index) => (
              <Draggable key={dataTag.tagId} draggableId={dataTag.tagId} index={index}>
                {(provided) => (
                  <DataTagCard
                    dataTag={dataTag}
                    provided={provided}
                    pinnedTags={pinnedTags}
                    handlePinTag={handlePinTag}
                    permission={permission}
                    onEditDataTag={onEditDataTag}
                    onViewDataTag={onViewDataTag}
                    onViewDataTagHistory={onViewDataTagHistory}
                    onDeleteDataTag={onDeleteDataTag}
                    getBusinessAreaColors={getBusinessAreaColors}
                  />
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
    </div>
  );
};

export default DataTagCardGrid;
